import { Box, ButtonBase, Typography, styled } from '@mui/material';

export const TilesContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'stretch',
  gap: theme.spacing(1),
  overflowX: 'auto',
  paddingBottom: theme.spacing(0.5)
}));

export const TileButton = styled(ButtonBase, {
  shouldForwardProp: (prop: string) => prop !== 'isActive'
})<{ isActive?: boolean }>(({ theme, isActive, disabled }) => ({
  display: 'flex',
  // The label sits above the icon rather than beside it, so it starts at the tile's left edge and has
  // the full width to itself.
  flexDirection: 'column',
  /**
   * Stretch rather than center, even though the content is centred: the label has to take the tile's
   * width so it can ellipsis when the strip is squeezed. Sized to its own text — which is what
   * alignItems: center would do — it would drag the tile as wide as the longest category and never
   * truncate. The centring is done with textAlign below and justifyContent on the value row instead.
   */
  alignItems: 'stretch',
  // Centres the content block vertically, since tiles stretch to a common height.
  justifyContent: 'center',
  /**
   * A floor rather than a fixed width, shared by every tile including Alle: tiles size to their own
   * label and only widen to this when the text is shorter. Kept fairly tight now the labels are short
   * (the "Afspraken" prefix was dropped), so the strip reads less wide.
   */
  minWidth: 110,
  padding: theme.spacing(1, 1.5),
  borderRadius: theme.shape.borderRadius,
  border: '1px solid',
  borderColor: isActive ? theme.palette.divider : 'transparent',
  backgroundColor: isActive
    ? theme.palette.background.paper
    : theme.palette.action.hover,
  boxShadow: isActive ? theme.shadows[1] : 'none',
  // Dimmed rather than hidden: an unselected tile is still a number worth reading, and a tile with
  // nothing behind it yet should look clearly out of reach.
  opacity: disabled ? 0.45 : isActive ? 1 : 0.65,
  // Centres the label inside its full-width box; see the alignItems note above.
  textAlign: 'center',
  transition: theme.transitions.create([
    'opacity',
    'box-shadow',
    'border-color'
  ]),
  '&:hover': {
    boxShadow: disabled ? 'none' : theme.shadows[1]
  }
}));

/**
 * The icon and the number, on the line under the label.
 *
 * Keeps the 15px set by eye earlier: that was the gap between the icon and the text beside it, and the
 * number is what sits there now. Still a raw value, because the theme's spacing unit is 4px and
 * spacing(3.75) would only disguise that 15 is off the scale.
 */
export const TileValueRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  // The icon and the number centre as one pair, so their midpoint lines up under the label rather than
  // the number sitting dead centre with the icon hanging off to its left.
  justifyContent: 'center',
  gap: 15,
  minWidth: 0
});

export const TileSeparator = styled(Box)(({ theme }) => ({
  width: '1px',
  alignSelf: 'stretch',
  margin: theme.spacing(0.5, 0.5),
  backgroundColor: theme.palette.divider,
  flexShrink: 0
}));

export const TileIcon = styled(Box, {
  shouldForwardProp: (prop: string) =>
    !['isActive', 'isAccent', 'color'].includes(prop)
})<{ isActive?: boolean; isAccent?: boolean; color?: string }>(
  ({ theme, isActive, isAccent, color }) => ({
    display: 'flex',
    flexShrink: 0,
    width: 18,
    color: !isActive
      ? theme.palette.text.disabled
      : isAccent
      ? theme.palette.primary.main
      : color ?? theme.palette.text.primary
  })
);

export const TileLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.625rem',
  fontWeight: 500,
  lineHeight: 1.3,
  color: theme.palette.text.secondary
}));

export const TileCount = styled(Typography, {
  shouldForwardProp: (prop: string) => prop !== 'isActive'
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  fontSize: '1.125rem',
  fontWeight: 600,
  lineHeight: 1.2,
  fontVariantNumeric: 'tabular-nums',
  color: isActive ? theme.palette.text.primary : theme.palette.text.secondary
}));
